// Inventory — Phase 5A page shell.
// Phase 10B — restyle to AutoMind 2050 design language.
//
// Keeps the page deliberately flat: one page owns filter state, fetch
// state, selection, and passes everything into small children. Server
// is the source of truth for permissions — we just surface/hide the
// editor controls.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { PageHeader } from '../../components/ui/page-header';
import { TabBar } from '../../components/ui/tab-bar';
import { SubTabBar } from '../../components/ui/sub-tab-bar';
import { DataCard } from '../../components/ui/data-card';
import { MetricCard } from '../../components/ui/metric-card';
import { SectionGrid } from '../../components/ui/section-grid';
import { StatusPill } from '../../components/ui/status-pill';
import { EmptyState } from '../../components/ui/empty-state';
import { Button } from '../../components/ui/button';
import { inventoryApi, dealersApi } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import InventoryFilters from './InventoryFilters';
import InventoryImportDialog from './InventoryImportDialog';
import InventoryDetailDialog from './InventoryDetailDialog';
import InventorySyncPanel from './InventorySyncPanel';

const EMPTY_FILTERS = {
  search: '', category: '', condition: '',
  availability_status: '', freshness_status: '',
  agent_usable: '',
  make: '', model: '',
};

const AVAILABILITY_TONE = {
  available: 'success',
  pending:   'warning',
  sold:      'neutral',
  unknown:   'neutral',
};

const CONDITION_TONE = {
  new:     'info',
  used:    'neutral',
  unknown: 'neutral',
};

function formatPrice(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatKm(n) {
  if (n === null || n === undefined) return '—';
  return `${Number(n).toLocaleString()} km`;
}

export default function Inventory() {
  const { user, hasRole } = useAuth();
  const isPlatformAdmin = hasRole('platform_admin');
  const canEdit = hasRole('platform_admin', 'dealer_owner', 'dealer_manager');

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [dealers, setDealers] = useState([]);
  const [dealerId, setDealerId] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [viewedDealerConfig, setViewedDealerConfig] = useState(null);
  const [statusTab, setStatusTab] = useState('all');
  const limit = 50;

  useEffect(() => {
    if (!isPlatformAdmin) return;
    dealersApi.list().then(setDealers).catch(() => {});
  }, [isPlatformAdmin]);

  const viewedDealerId = isPlatformAdmin ? dealerId : user?.dealer_id;
  useEffect(() => {
    if (!viewedDealerId) { setViewedDealerConfig(null); return; }
    let cancelled = false;
    dealersApi.get(viewedDealerId)
      .then((d) => { if (!cancelled) setViewedDealerConfig(d?.config || null); })
      .catch(() => { if (!cancelled) setViewedDealerConfig(null); });
    return () => { cancelled = true; };
  }, [viewedDealerId]);

  const syncGateReason = (() => {
    if (!canEdit) return 'Read-only role — cannot sync.';
    if (!viewedDealerId) return 'Select a dealer to sync.';
    const cfg = viewedDealerConfig;
    const inv = cfg?.inventory || {};
    const fc = cfg?.integrations?.firecrawl || {};
    if (!['website_crawl', 'both'].includes(inv.inventory_mode)) {
      return `Inventory mode is "${inv.inventory_mode || 'none'}" — website_crawl or both required.`;
    }
    if (!fc.firecrawl_enabled) return 'Firecrawl is not enabled for this dealer.';
    return '';
  })();
  const canSync = canEdit && !syncGateReason;
  const syncDisabledReason = syncGateReason;

  // Sync the active TabBar selection into filters.availability_status.
  useEffect(() => {
    setFilters((f) => {
      const next = statusTab === 'all' ? '' : statusTab;
      if (f.availability_status === next) return f;
      return { ...f, availability_status: next };
    });
    setOffset(0);
  }, [statusTab]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...filters, limit, offset };
      if (isPlatformAdmin && dealerId) params.dealer_id = dealerId;
      if (params.agent_usable === 'true') params.agent_usable = true;
      else if (params.agent_usable === 'false') params.agent_usable = false;
      else delete params.agent_usable;
      Object.keys(params).forEach((k) => {
        if (params[k] === '' || params[k] === null || params[k] === undefined) {
          delete params[k];
        }
      });
      const res = await inventoryApi.list(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err);
      toast.error(err.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [filters, offset, isPlatformAdmin, dealerId]);

  useEffect(() => { load(); }, [load]);

  const onOpen = async (item) => {
    try {
      const fresh = await inventoryApi.get(item.id);
      setDetail(fresh);
      setDetailOpen(true);
    } catch (err) {
      toast.error(err.message || 'Failed to open item');
    }
  };

  const showImport = useMemo(() => {
    if (!canEdit) return false;
    if (isPlatformAdmin) return !!dealerId;
    return true;
  }, [canEdit, isPlatformAdmin, dealerId]);

  // KPIs from the items currently loaded.
  const kpis = useMemo(() => {
    return {
      total: total || items.length,
      available: items.filter((i) => i.availability_status === 'available').length,
      newCount: items.filter((i) => i.condition === 'new').length,
      used: items.filter((i) => i.condition === 'used').length,
    };
  }, [items, total]);

  const statusTabs = [
    { id: 'all',       label: 'All' },
    { id: 'available', label: 'Available' },
    { id: 'pending',   label: 'Pending' },
    { id: 'sold',      label: 'Sold' },
  ];

  const conditionSubTabs = [
    { id: '',        label: 'All' },
    { id: 'new',     label: 'New' },
    { id: 'used',    label: 'Used' },
  ];

  const headerActions = (
    <>
      <button
        type="button"
        onClick={load}
        disabled={loading}
        className="btn-2050 btn-ghost"
        aria-label="Refresh"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        Refresh
      </button>
      {showImport && (
        <InventoryImportDialog
          dealerId={isPlatformAdmin ? dealerId : undefined}
          onImported={load}
        />
      )}
    </>
  );

  const renderTable = () => {
    if (error) {
      return (
        <EmptyState
          title="Couldn't load inventory"
          description="Unable to load right now."
        />
      );
    }
    if (!items.length) {
      const hint = isPlatformAdmin && !dealerId
        ? 'Pick a dealer above to view their inventory, or leave blank to see all dealers.'
        : 'No vehicles match the current filters.';
      return (
        <EmptyState
          title="No inventory"
          description={hint}
        />
      );
    }
    return (
      <div className="overflow-x-auto -mx-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-[0.16em] text-ink-500 bg-ink-50/60 dark:bg-ink-900/40">
              <th className="text-left px-6 py-3 font-medium">Stock</th>
              <th className="text-left px-6 py-3 font-medium">Vehicle</th>
              <th className="text-left px-6 py-3 font-medium">Color</th>
              <th className="text-right px-6 py-3 font-medium">Price</th>
              <th className="text-right px-6 py-3 font-medium">Mileage</th>
              <th className="text-left px-6 py-3 font-medium">Condition</th>
              <th className="text-left px-6 py-3 font-medium">Status</th>
              <th className="text-right px-6 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
            {items.map((it) => (
              <tr
                key={it.id}
                className="row-2050 hover:bg-ink-50/60 dark:hover:bg-ink-900/40 align-top"
              >
                <td className="px-6 py-3 nums text-xs">
                  <div className="text-ink-900 dark:text-ink-50">{it.stock_number || '—'}</div>
                  {it.vin && (
                    <div className="text-ink-500">{it.vin}</div>
                  )}
                </td>
                <td className="px-6 py-3">
                  <div className="font-medium text-ink-900 dark:text-ink-50">
                    {[it.year, it.make, it.model].filter(Boolean).join(' ') || '—'}
                  </div>
                  {it.trim && (
                    <div className="text-xs text-ink-500 mt-0.5">{it.trim}</div>
                  )}
                </td>
                <td className="px-6 py-3 text-ink-500">
                  {it.exterior_color || '—'}
                </td>
                <td className="px-6 py-3 nums text-right text-ink-900 dark:text-ink-50">
                  <span className="text-ink-500 mr-0.5">$</span>{formatPrice(it.price)}
                </td>
                <td className="px-6 py-3 nums text-right text-ink-500">
                  {formatKm(it.mileage_km)}
                </td>
                <td className="px-6 py-3">
                  {it.condition ? (
                    <StatusPill
                      tone={CONDITION_TONE[it.condition] || 'neutral'}
                      label={it.condition.charAt(0).toUpperCase() + it.condition.slice(1)}
                    />
                  ) : (
                    <span className="text-ink-400">—</span>
                  )}
                </td>
                <td className="px-6 py-3">
                  <StatusPill
                    tone={AVAILABILITY_TONE[it.availability_status] || 'neutral'}
                    label={it.availability_status
                      ? it.availability_status.charAt(0).toUpperCase() + it.availability_status.slice(1)
                      : '—'}
                    dot
                  />
                </td>
                <td className="px-6 py-3 text-right whitespace-nowrap">
                  <div className="inline-flex gap-1">
                    <button
                      type="button"
                      className="btn-2050 btn-secondary"
                      onClick={() => onOpen(it)}
                    >
                      View
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        className="btn-2050 btn-ghost"
                        onClick={() => onOpen(it)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Stock"
        title="Inventory"
        description="Vehicles available for the AI agent to surface."
        actions={headerActions}
      />

      <SectionGrid cols={4}>
        <MetricCard label="Total"     value={kpis.total}     helperText="In view" />
        <MetricCard label="Available" value={kpis.available} helperText="Ready to sell" />
        <MetricCard label="New"       value={kpis.newCount}  helperText="New condition" />
        <MetricCard label="Used"      value={kpis.used}      helperText="Pre-owned" />
      </SectionGrid>

      <TabBar value={statusTab} onChange={setStatusTab} items={statusTabs} />

      {isPlatformAdmin && (
        <DataCard title="Dealer scope" description="Pick a dealer to view their inventory.">
          <Input
            list="inv-dealers"
            placeholder="Select a dealer (id) to scope the view"
            value={dealerId}
            onChange={(e) => { setDealerId(e.target.value); setOffset(0); }}
          />
          <datalist id="inv-dealers">
            {dealers.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </datalist>
        </DataCard>
      )}

      <DataCard
        title="Sync"
        description="Run a Firecrawl sync to refresh listings from the dealer website."
      >
        <InventorySyncPanel
          dealerId={isPlatformAdmin ? dealerId : user?.dealer_id}
          canSync={canSync}
          disabledReason={syncDisabledReason}
          onSynced={load}
        />
      </DataCard>

      <DataCard
        title="Filters"
        description="Refine by condition, availability, freshness, and more."
        actions={
          <SubTabBar
            value={filters.condition || ''}
            onChange={(v) => setFilters({ ...filters, condition: v })}
            items={conditionSubTabs}
          />
        }
      >
        <InventoryFilters
          filters={filters}
          onChange={(f) => { setFilters(f); setOffset(0); }}
        />
      </DataCard>

      <DataCard
        title="Inventory"
        description={`${items.length} of ${total || items.length} shown`}
        bodyClassName="p-0"
        loading={loading && items.length === 0}
      >
        {renderTable()}
      </DataCard>

      <div className="flex items-center justify-between text-xs text-ink-500">
        <span className="nums">
          Showing {items.length} of {total || items.length}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm" variant="outline"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
          >
            Prev
          </Button>
          <Button
            size="sm" variant="outline"
            disabled={items.length < limit}
            onClick={() => setOffset(offset + limit)}
          >
            Next
          </Button>
        </div>
      </div>

      <InventoryDetailDialog
        item={detail}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        canEdit={canEdit}
        isPlatformAdmin={isPlatformAdmin}
        onChanged={load}
      />
    </div>
  );
}
